/**
 * v2.3 P2 (D-77): the sort menus on /leads and on a Curative board.
 *
 * An option is a (key, order) pair the API already understands. "Default" on leads is NO sort param —
 * the server's hot-first-then-due order (v2.2 P3.1) — and "Manual" on a board is its stage-entry order,
 * the only mode in which a card's place in its column is not computed.
 */

export type SortOption = { id: string; label: string; sort: string | null; order: "asc" | "desc" };

export const LEAD_SORTS: SortOption[] = [
  { id: "default", label: "Default (hot, due soonest)", sort: null, order: "asc" },
  { id: "activity", label: "Latest activity", sort: "last_activity_at", order: "desc" },
  { id: "newest", label: "Newest added", sort: "created_at", order: "desc" },
  { id: "oldest", label: "Oldest added", sort: "created_at", order: "asc" },
  { id: "due", label: "Due soonest", sort: "next_action_due", order: "asc" },
  { id: "touched", label: "Last touched", sort: "last_touched_at", order: "desc" },
];

export const BOARD_SORTS: SortOption[] = [
  { id: "manual", label: "Manual", sort: null, order: "asc" },
  { id: "activity", label: "Latest activity", sort: "last_activity_at", order: "desc" },
  { id: "newest", label: "Newest added", sort: "created_at", order: "desc" },
  { id: "oldest", label: "Oldest added", sort: "created_at", order: "asc" },
  { id: "value", label: "Highest value", sort: "value", order: "desc" },
  { id: "hot", label: "Hot first", sort: "hot", order: "desc" },
];

/** The option a (sort, order) pair is, or null for a column sort the menu has no name for. */
export function matchSort(
  options: SortOption[],
  sort: string | null | undefined,
  order: string | null | undefined,
): SortOption | null {
  const s = sort ?? null;
  return (
    options.find((o) => o.sort === s && (s === null || o.order === (order ?? "asc"))) ?? null
  );
}

export const LEADS_SORT_KEY = "leads.sort";

/** localStorage can throw (private mode, blocked storage); a missing preference is just Default. */
export function readStoredSort(key: string): { sort: string | null; order: "asc" | "desc" } | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw) as { sort?: unknown; order?: unknown };
    const sort = typeof v.sort === "string" ? v.sort : null;
    const order = v.order === "desc" ? "desc" : "asc";
    return { sort, order };
  } catch {
    return null;
  }
}

export function storeSort(key: string, sort: string | null, order: "asc" | "desc"): void {
  try {
    window.localStorage.setItem(key, JSON.stringify({ sort, order }));
  } catch {
    // A preference that cannot be stored is a preference for this page view only.
  }
}
